import { useState } from 'react';
import { useMutation } from 'react-query';
import {
  Alert, Box, Button, Card, CardContent, Chip, MenuItem, Paper, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import { mutationErrorMessage } from '../../lib/mutation-error';
import { itineraryToPayload } from '../../lib/groupPayload';
import { endTimeEdit, endTimeProblem } from '../../lib/itineraryTimes';
import { buildItineraryChangeNotice, type ItineraryChangeNotice } from '../../lib/changeNotice';
import ChangeNoticeDialog from './changes/ChangeNoticeDialog';
import {
  formatRangeInZone,
  resolveTripTimeZone,
  wallClockToIso,
  isoToWallClock,
} from '../../lib/tripTime';
import PlacePicker from './PlacePicker';
import CancelItineraryDialog from './CancelItineraryDialog';
import {
  EMPTY_BINDING,
  isBindingStale,
  keepBinding,
  pickedBinding,
  placeFieldsForSave,
  storedBinding,
} from '../../lib/placeBinding';
import {
  ITINERARY_AUDIENCES,
  ITINERARY_AUDIENCE_HINTS,
  ITINERARY_AUDIENCE_LABELS,
  canCancel,
  itineraryAudience,
  itineraryStatus,
  visibilityBadge,
  visibilityFields,
  visibilityNote,
} from '../../lib/itineraryVisibility';
import type { ItineraryAudience, ItineraryStatus } from '../../lib/itineraryVisibility';
import type { GroupDetail as GroupDetailType, GroupItineraryItem } from '../../types/groups';

const EMPTY_ITINERARY_FORM = { title: '', description: '', locationName: '', startsAt: '', endsAt: '' };

/*
  A new tile keeps the server's own defaults: published, everyone.

  Defaulting it to Draft would be the same bug from the other side — a host who
  never notices the control writes the weekend into a lane nobody reads, and
  nothing on the guest page says why it is empty. What was missing was never a
  different default; it was being able to see the default and change it before
  pressing Save.
*/
const NEW_TILE_STATUS: ItineraryStatus = 'published';
const NEW_TILE_AUDIENCE: ItineraryAudience = 'all';

export default function ItineraryTab({ group, onChanged }: { group: GroupDetailType; onChanged: () => void }) {
  const itineraryMutation = useMutation((body: any) => groupsApi.upsertItineraryItem(group.id, body), { onSuccess: onChanged });
  const saving = itineraryMutation.isLoading;
  const error = mutationErrorMessage(itineraryMutation.error);

  const [form, setForm] = useState(EMPTY_ITINERARY_FORM);
  /*
    The endpoint has always been an upsert — UpsertItineraryItemRequest carries
    an Id and preserves omitted fields on update. This screen just never sent
    one, so every save minted a new tile and a typo was permanent. Holding the
    id here is the whole fix.
  */
  const [editing, setEditing] = useState<GroupItineraryItem | null>(null);
  const editingId = editing?.id ?? null;
  /*
    The place plus the location text it was captured against.

    A bare place was not enough. A host who opens a tile, retypes the location
    and saves without rebinding used to keep the old coordinates — that is the
    Sunday lunch tile that named Bairro Alto Hotel and pinned Costa da Caparica.
    Binding the two together is what lets the save tell "still the same venue"
    from "the pin is left over".
  */
  const [binding, setBinding] = useState(EMPTY_BINDING);
  const [cancelling, setCancelling] = useState<GroupItineraryItem | null>(null);
  /*
    Set right after a save succeeds, never before — see lib/changeNotice.ts.
    Studio must never decide on its own that guests should be told something;
    it only ever decides whether to ASK the host, and only once the write that
    would need announcing has already landed.
  */
  const [changeNotice, setChangeNotice] = useState<ItineraryChangeNotice | null>(null);
  /*
    An item may carry its own zone that differs from the trip's. Reading it back
    in that zone but saving in the trip's would reinterpret the same wall-clock
    against a different offset — an 18:00 New York item on a Lisbon trip would
    become 18:00 Lisbon just by pressing Save. Whatever zone the form was filled
    from is the zone it is written back in.
  */
  const [editingTimeZone, setEditingTimeZone] = useState<string | null>(null);
  /*
    Who this tile is for, held beside the rest of the form.

    Both columns have existed since the timeline foundation and both guest reads
    already filter on them; this screen simply never sent either, so every tile
    was born published-to-everyone and Save was the same keystroke as Publish.
  */
  const [status, setStatus] = useState<ItineraryStatus>(NEW_TILE_STATUS);
  const [audience, setAudience] = useState<ItineraryAudience>(NEW_TILE_AUDIENCE);
  /*
    Said under the box, not in a browser dialog.

    An alert delivers three sentences into a widget that renders generic
    OK/Cancel, dismisses to a form that still has the empty box, and re-fires on
    the next Save — so the host's only escape was to retype a time they could no
    longer see. Inline, the message sits beside the field it is about and the
    Restore button puts the stored value back.
  */
  const [endClearError, setEndClearError] = useState('');
  const { timeZone, isFallback } = resolveTripTimeZone(group.timeZone);
  // Adding (not editing) needs a real trip zone; see the warning above the save button.
  const needsTripZone = isFallback && !editingId;
  const inviteOnly = group.accessMode === 'invite_only';

  /*
    Asks, never sends. `group.published` gates it because there is nobody to
    ask the question about a broadcast to on an unpublished group — sending is
    refused there regardless — and asking anyway would be a dead-end dialog.
  */
  const offerChangeNotice = (before: GroupItineraryItem | null, after: GroupItineraryItem) => {
    if (!group.published) return;
    const notice = buildItineraryChangeNotice(before, after, timeZone);
    if (notice) setChangeNotice(notice);
  };

  const startEditing = (item: GroupItineraryItem) => {
    setEditing(item);
    setEditingTimeZone(item.timeZone || timeZone);
    // Seeded from the stored row, so opening a private tile and pressing Save
    // cannot quietly promote it to the guest page.
    setStatus(itineraryStatus(item));
    setAudience(itineraryAudience(item));
    setEndClearError('');
    setForm({
      title: item.title ?? '',
      description: item.description ?? '',
      locationName: item.locationName ?? '',
      // Read back in the trip's zone, not the browser's: a host in Chicago
      // opening a Lisbon dinner must not see 12:30 and move it by saving.
      startsAt: isoToWallClock(item.startsAt, item.timeZone || timeZone),
      endsAt: isoToWallClock(item.endsAt, item.timeZone || timeZone),
    });
    // Shown as already-chosen so an edit does not look like the place was lost.
    // A stored item with no address predates the picker; it presents as unset and
    // the host can pick one, which is the only way those ever get filled in.
    // Bound to the location text as saved: nobody has confirmed during this edit
    // that the pin and that text still describe the same venue.
    setBinding(
      storedBinding(
        item.address || item.googlePlaceId || item.latitude != null
          ? {
              tripsbyLocationId: item.tripsbyLocationId,
              googlePlaceId: item.googlePlaceId,
              name: item.locationName || item.title,
              address: item.address,
              latitude: item.latitude,
              longitude: item.longitude,
              businessPhone: item.businessPhone,
              websiteUrl: item.websiteUrl,
              mapsUrl: item.mapsUrl,
            }
          : null,
        item.locationName ?? '',
      ),
    );
  };

  const stopEditing = () => {
    setEditing(null);
    setBinding(EMPTY_BINDING);
    setEditingTimeZone(null);
    setForm(EMPTY_ITINERARY_FORM);
    setStatus(NEW_TILE_STATUS);
    setAudience(NEW_TILE_AUDIENCE);
    setEndClearError('');
  };

  const pinIsStale = isBindingStale(binding, form.locationName);
  /*
    The zone the boxes are FILLED and SAVED in, which for an edit is the item's
    own. The labels used to name the trip's zone unconditionally, so a New York
    item on a Lisbon trip showed New York wall-clock under a "(Europe/Lisbon)"
    label — and a host reading 18:00 as Lisbon and "correcting" it moved the
    event five hours.
  */
  const formZone = editingTimeZone || timeZone;
  // One evaluation, three consumers: the field's error, its helper text, and
  // the Save button. It was called three times per render on a component that
  // re-renders on every keystroke.
  const endProblem = endTimeProblem(form.startsAt, form.endsAt);
  const zoneHelper = isFallback ? 'This trip has no time zone set — using yours. Set it on the Overview tab.' : 'Local time at the trip.';

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800} gutterBottom>
          {editingId ? 'Edit itinerary tile' : 'Add itinerary tile'}
        </Typography>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <TextField label="Location" value={form.locationName} onChange={(e) => setForm({ ...form, locationName: e.target.value })} />
          <TextField
            label={`Starts at (${formZone})`}
            type="datetime-local"
            value={form.startsAt}
            onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
            helperText={zoneHelper}
            InputLabelProps={{ shrink: true }}
          />
          {/*
            Optional, and it stays optional. "Drinks from 8" is a real plan and
            an end time invented for it is worse than none — it is what turns a
            calendar export into a wall of arbitrary blocks.
          */}
          <TextField
            label={`Ends at (${formZone})`}
            type="datetime-local"
            value={form.endsAt}
            onChange={(e) => { setEndClearError(''); setForm({ ...form, endsAt: e.target.value }); }}
            error={Boolean(endProblem) || Boolean(endClearError)}
            helperText={
              endProblem
              || endClearError
              // The same zone caveat the start carries. Without it a host in
              // Chicago reads "Ends at (America/Chicago)" as a statement about
              // the trip rather than a guess about them.
              || (isFallback ? zoneHelper : 'Optional. Guests see a range, and it sets the calendar entry\'s length.')
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Box>
        {/*
          Replaces the latitude and longitude boxes that used to sit here. Asking
          a host for a decimal degree is asking the wrong person for the wrong
          thing, and the map stayed empty because nobody answered.
        */}
        <Box sx={{ mt: 2 }}>
          <PlacePicker
            groupId={group.id}
            near={group.locationName}
            value={binding.place}
            stale={pinIsStale}
            onKeep={() => setBinding((current) => keepBinding(current, form.locationName))}
            onChange={(next) => {
              // The name is the host's to edit — a venue they picked may be
              // "Hotel do Chiado" while the tile should read "Check in". So the
              // pin binds to the text as it stands after the pick, and later
              // renames do not re-raise the warning: the host has seen this venue.
              const locationName = form.locationName || next?.name || '';
              setBinding(pickedBinding(next, locationName));
              if (next && !form.locationName) {
                setForm((current) => ({ ...current, locationName: next.name || '' }));
              }
            }}
            disabled={saving}
          />
        </Box>
        {/*
          Who sees this, before it is saved rather than after.

          Both controls are on screen at all times, showing the real defaults —
          not folded behind "advanced". A setting a host has to go looking for is
          one they discover the morning after 137 people read a placeholder.
        */}
        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' }, gap: 2, alignItems: 'start' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Status
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={status === 'cancelled' ? null : status}
              onChange={(_, next: ItineraryStatus | null) => { if (next) setStatus(next); }}
              disabled={saving || status === 'cancelled'}
            >
              <ToggleButton value="draft">Draft</ToggleButton>
              <ToggleButton value="published">Published</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField
            select
            size="small"
            label="Who sees it"
            value={audience}
            disabled={saving}
            onChange={(e) => setAudience(e.target.value as ItineraryAudience)}
            helperText={visibilityNote(status, audience, inviteOnly)}
            sx={{ minWidth: 220 }}
          >
            {ITINERARY_AUDIENCES.map((option) => (
              <MenuItem key={option} value={option}>
                {ITINERARY_AUDIENCE_LABELS[option]} — {ITINERARY_AUDIENCE_HINTS[option]}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {/*
          The way out of the refusal. Without it the host is left holding an
          empty box, no longer able to see the time they cleared, and every
          Save press repeats the message.
        */}
        {endClearError && editing?.endsAt ? (
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              setForm((current) => ({
                ...current,
                endsAt: isoToWallClock(editing.endsAt, formZone),
              }));
              setEndClearError('');
            }}
          >
            Restore the stored end time
          </Button>
        ) : null}
        {/*
          No new events on a trip with no time zone. Every event stores its own
          zone and that zone wins on the guest page, so an event added here now
          would be filed under the host's own zone for good — the Lisbon trip
          that showed "8:00 PM · America/Chicago" to its guest.
        */}
        {needsTripZone ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Set this trip&apos;s time zone on the Overview tab before adding events. Otherwise their
            times would be saved in your own zone ({timeZone}).
          </Alert>
        ) : null}
        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            disabled={saving || !form.title || Boolean(endProblem) || needsTripZone}
            onClick={async () => {
            // Clearing a stored end time is not something the endpoint can
            // express — `ends_at = COALESCE(@EndsAt, ends_at)` reads null as
            // "preserve" — so the save would report success and keep the old
            // value. Refuse out loud rather than lie about what happened.
            const storedEnd = editing ? isoToWallClock(editing.endsAt, formZone) : '';
            const endEdit = endTimeEdit(form.endsAt, storedEnd);
            if (endEdit.kind === 'cannot_clear') {
              setEndClearError(
                'An end time can be changed but not removed — the API reads an empty value as '
                + '"leave it alone". Put the real finish in, or restore the stored one.',
              );
              return;
            }
            /*
              A wall clock the parser refuses must stop the save, not shrink to
              `undefined`.

              `wallClockToIso` returns null for anything outside
              `YYYY-MM-DDTHH:mm` — and Chrome's year spinner will hand you
              '202600-08-15T22:00' from one stray keystroke. Sending undefined
              for endsAt means COALESCE preserves the old value; sending it for
              startsAt is worse, because starts_at is assigned outright and the
              tile loses its time. Either way the save reports success and the
              form clears. This is the same silent-preserve the module exists to
              prevent, and it was sitting two lines under the comment naming it.
            */
            // formZone is the zone the boxes were FILLED from — the item's own on an
            // edit, the trip's on a new tile — so it is the zone they write back in.
            const startIso = form.startsAt ? wallClockToIso(form.startsAt, formZone) : '';
            const endIso = endEdit.kind === 'send' ? wallClockToIso(endEdit.wallClock, formZone) : '';
            if ((form.startsAt && !startIso) || (endEdit.kind === 'send' && !endIso)) {
              setEndClearError('That date could not be read. Check the year — four digits.');
              return;
            }
            // Held before the save so offerChangeNotice can diff against the
            // tile as it stood BEFORE this write — `editing` itself is cleared
            // by stopEditing() a few lines down.
            const before = editing;
            try {
              const saved = await itineraryMutation.mutateAsync({
                // The stored tile first, because the upsert's UPDATE assigns
                // some columns outright rather than COALESCEing them — an edit
                // that sends only the form blanks the call-to-action and resets
                // the display order. See lib/groupPayload.
                ...(editing ? itineraryToPayload(editing) : {}),
                ...form,
                // Sending the id turns the upsert into an update; omitting it
                // inserts, which is the add case.
                id: editingId ?? undefined,
                // Offset-qualified against the zone above. A bare
                // "2026-08-15T17:30" is bound with the API process's zone (UTC
                // on Cloud Run), which silently shifts every summer event.
                startsAt: startIso || undefined,
                // Only when it changed. Sending the stored value back is
                // harmless but sending an empty one is not — see endTimeEdit.
                endsAt: endIso || undefined,
                timeZone: formZone,
                // From the picked place, never from typed coordinates — and
                // cleared outright when the location text moved off the place
                // this pin came from. See lib/placeBinding for why a wrong pin
                // is worse than no pin.
                ...placeFieldsForSave(binding, form.locationName),
                // Both, always. Null means "preserve" to the API, which is how
                // this screen used to be unable to say anything about
                // visibility at all — and how every tile ended up published.
                ...visibilityFields(status, audience),
              });
              // Only now: clearing before the save landed threw away the host's
              // typing on any failure, leaving an error beside an empty form
              // and nothing to retry with.
              stopEditing();
              // After the save succeeded — never before, and never on a mere
              // field edit. See lib/changeNotice.ts for what counts.
              offerChangeNotice(before, saved);
            } catch {
              /* mutationErrorMessage renders it; keep the form so it can be retried */
            }
          }}>
            {/* The button says what pressing it does. "Add tile" beside a
                Published toggle is the same silence that made Save mean
                Publish. */}
            {editingId
              ? 'Save changes'
              : status === 'draft' ? 'Save as draft' : 'Add and publish'}
          </Button>
          {editingId ? (
            <Button variant="text" disabled={saving} onClick={stopEditing}>Cancel</Button>
          ) : null}
        </Stack>
      </Paper>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {group.itineraryItems.map((item) => (
          <Card key={item.id} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography fontWeight={800}>{item.title}</Typography>
              {/*
                Only when the card and the guest page disagree. Published to
                everyone is the norm and carries nothing — a badge on every card
                is a badge on none, and the two that matter stop being read.
              */}
              {(() => {
                const badge = visibilityBadge(item);
                return badge ? (
                  <Chip
                    size="small"
                    label={badge.label}
                    color={badge.tone === 'default' ? 'default' : badge.tone}
                    variant={badge.tone === 'warning' ? 'filled' : 'outlined'}
                    sx={{ mt: 0.75 }}
                  />
                ) : null;
              })()}
              {/* A time the host can't see is a time the host can't catch. */}
              {/*
                One formatter owns both halves and names the zone once, at the
                end. Pasting a formatted start onto a formatted end put the zone
                inside the range — "7:30 PM · Europe/Lisbon – 10:00 PM" — which
                reads as running from a place to a time. It also could not tell
                a missing end from an unusable one, and rendered a dangling dash
                for the second.
              */}
              <Typography variant="body2" color="text.secondary">
                {formatRangeInZone(item.startsAt, item.endsAt, item.timeZone || timeZone, 'No time set')}
              </Typography>
              <Typography variant="body2" color="text.secondary">{item.locationName}</Typography>
              {item.latitude != null && item.longitude != null ? (
                <Typography variant="caption" color="text.secondary">{item.latitude}, {item.longitude}</Typography>
              ) : null}
              <Typography variant="body2" sx={{ mt: 1 }}>{item.description}</Typography>
              {item.status === 'cancelled' ? (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Cancelled{item.cancellationReason ? `: ${item.cancellationReason}` : ''}. Guests
                  see this in place of the plan.
                </Alert>
              ) : (
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Button size="small" disabled={saving} onClick={() => startEditing(item)}>
                    Edit
                  </Button>
                  {/*
                    Promoting a draft without opening the form. The whole point
                    of the lane is sketching a weekend, and a weekend goes live
                    a tile at a time on the day — not through eight round trips
                    into an edit form.
                  */}
                  {itineraryStatus(item) === 'draft' ? (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={saving}
                      onClick={async () => {
                        try {
                          // The whole tile, not a bare { id, status }: the upsert
                          // assigns starts_at, the CTA and the display order
                          // outright, so a minimal body publishes the tile with
                          // its time erased.
                          const saved = await itineraryMutation.mutateAsync(
                            itineraryToPayload(
                              item,
                              visibilityFields('published', itineraryAudience(item)),
                            ),
                          );
                          offerChangeNotice(item, saved);
                        } catch {
                          /* mutationErrorMessage renders it */
                        }
                      }}
                    >
                      Publish
                    </Button>
                  ) : null}
                  {/*
                    Cancel, not delete. Guests have already seen this on their
                    timeline, so it stays as history with the reason attached —
                    which is why the dialog asks for one rather than defaulting.

                    Never on a draft. The guest read admits
                    `status IN ('published','cancelled')`, so cancelling a tile
                    nobody has seen PUBLISHES it, as a cancelled event, to
                    guests with no plan to attach it to.
                  */}
                  {canCancel(itineraryStatus(item)) ? (
                    <Button size="small" color="error" disabled={saving} onClick={() => setCancelling(item)}>
                      Cancel event
                    </Button>
                  ) : null}
                </Stack>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {cancelling ? (
        <CancelItineraryDialog
          item={cancelling}
          onClose={() => setCancelling(null)}
          onCancelled={(cancelledItem) => {
            const before = cancelling;
            setCancelling(null);
            onChanged();
            offerChangeNotice(before, cancelledItem);
          }}
          groupId={group.id}
        />
      ) : null}

      <ChangeNoticeDialog
        open={Boolean(changeNotice)}
        onClose={() => setChangeNotice(null)}
        group={group}
        notice={changeNotice}
      />
    </Stack>
  );
}
